import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0125 from "../lesson-25/lesson";
const linkLesson0125 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0125.id);

const theory = ` <h3 class=sectionHead>Dissipation mechanisms</h3>
<h4 class=subsectionHead>Consequence of the dissipation of energy on intensity</h4>
<p>
   When a wave propagates, part of the acoustic energy is dissipated. For this wave dissipation results in a decrease in amplitude which can be expressed in dB/m or %/m. Denoting
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
      <mi>α</mi>
   </math>
   the relative energy loss per unit length, we can write
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mn>2</mn>
            <mi>α</mi>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
                  <mi>I</mi>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <mo class=MathClass-rel>|</mo>
                  <mi>I</mi>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <mi>x</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   If the medium is uniform,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
   </math>
   is constant and after integration we obtain
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>I</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>±</mo>
                  <mn>2</mn>
                  <mi>α</mi>
                  <mi>x</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   stands for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>I</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
   </math>. In this equation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>+</mo>
   </math>
   is for a wave propagating to the left while
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>−</mo>
   </math>
   is for a wave propagating to the right.
<p>So, for a wave propagating in a uniform medium, dissipation results in an exponential decay of the amplitude of the intensity.
<h4 class=subsectionHead>How to include damping in the solutions of wave equation</h4>
<p>
   Strictly speaking, to take into account dissipative processes one should integrate the various forms of loss in the equations of acoustics. This is a pretty tough job whose presentation is beyond the scope of this course which is a first approach to physical acoustics. To account for the losses it can be shown that results obtained in the absence of losses remain valid considering a complex wave number (this approach is limited to the frequency domain because the losses are in general frequency dependent). We have seen that the energy of the progressive plane wave decreases exponentially. But the intensity being the product of the pressure and velocity which also have their amplitude decreasing during the propagation. Indeed, for a progressive wave we have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>I</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mo class=MathClass-rel>|</mo>
            <mi>A</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
            <msup>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is the pressure amplitude, we deduce that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>±</mo>
            <mi>α</mi>
            <mi>x</mi>
         </mrow>
      </msup>
   </math>
   with alpha the so called absorption coefficient. Finally for pressure we have
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>±</mo>
                  <mi>j</mi>
                  <mi>k</mi>
                  <mi>x</mi>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>±</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi>j</mi>
                  <mi>k</mi>
                  <mi>x</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi>α</mi>
                  <mi>x</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   Setting
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>k</mi>
      <mo class=MathClass-bin>−</mo>
      <mi>j</mi>
      <mi>α</mi>
   </math>
   the complex wave number we finally can write
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>±</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
                  <mi>x</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(4)
</table>
<p>It is noted that this result is (apparently) perfectly identical to that of the plane wave without losses with a constant amplitude except that the real wave number is replaced by a complex wave number. One must be aware that the amplitude is constant only in appearance because the decrease in amplitude is ”hidden” in the imaginary part of the wave number. The value of the absorption coefficient depends on the problem considered: for free field this parameter is quite difficult to estimate. In a pipe it can be calculated fairly accurately.
<h4 class=subsectionHead>Absorption in free air</h4>
<p>For a plane wave propagating in the infinite space (air) there are several dissipation mechanisms.
<h5 class=subsubsectionHead>Classical absorption</h5>
<p>This absorption is due to the viscosity of the fluid and heat transfer. It is proportional to the square of the frequency. In practice it is really significant above 10 kHz.
<h5 class=subsubsectionHead>Molecular absorption</h5>
<p>
   This dissipation mechanism has been late understood in the fifties. It is in fact the main source of attenuation in the air while the classical absorption is often negligible. It is due to the natural vibrations of the molecules that make up air (principally oxygen
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>O</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   and nitrogen
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>N</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   ). This is a term that is also highly dependent on the moisture content since its value can vary on a factor of 20 depending on temperature and humidity.<br class=newline>In summary, it is difficult to know a priori the absorption in the air as it can vary greatly. It is possible to approximately calculate the absorption as a function of the frequency provided to know the atmospheric pressure, temperature and humidity (assuming that these quantities are uniform). The norm ISO 9613-2 describes a method for calculating the attenuation of sound during propagation outdoors in order to predict the levels of environmental noise at a distance from a variety of sources. The method predicts the equivalent continuous A-weighted sound pressure level under meteorological conditions.
<h4 class=subsectionHead>Absorption in a pipe</h4>
<p>
   In a tube, volume absorption is generally negligible compared to the visco-thermal absorption on the wall. This absorption is related to heat transfer between the fluid and the wall (assumed conductive) and the viscous friction between the fluid and the wall. In a tube of radius
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   it can be considered that the absorption coefficient for the intensity with reasonable accuracy at 20<span class=tcrm-1000>°</span>C in the air is given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>α</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>3</mn>
            <mo class=MathClass-punc>.</mo>
            <mn>0</mn>
            <mi> </mi>
            <mn>1</mn>
            <msup>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>5</mn>
               </mrow>
            </msup>
            <msqrt>
               <mrow>
                  <mi>f</mi>
               </mrow>
            </msqrt>
            <mo class=MathClass-bin>∕</mo>
            <mi>R</mi>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
   </math>
   the frequency in Hz and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   the radius in m, result being in m<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <msup>
    <mrow>
    <mn></mn>
    </mrow>
    <mrow>
    <mo class=MathClass-bin>-</mo>
    <mn>1</mn>
    </mrow>
    </msup>
   </math>. If the tube has not a circular cross section, the radius
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   can be replaced by an equivalent radius
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
            <mi>q</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi>A</mi>
         </mrow>
      </mfrac>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>P</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
   </math>
   are respectively the perimeter and the area of the cross section.
<h4 class=subsectionHead>Dispersion</h4>
<p>Often the dissipation resulting in an attenuation of the wave is accompanied by the phenomenon of dispersion. It is said that there is dispersion, or that the medium is dispersive, when the speed of the wave is a function of the frequency: different frequencies do not propagate at the same speed (see <a href="${linkLesson0125}" target="_blank">Lesson 1.25</a>). Thus a short duration pulse sees its duration increased during propagation. This phenomenon is particularly noticeable in stormy weather: when lightning falls near an observer (listener), it means a very brief and very violent snap. When lightning occurs at a greater distance means a dull and relatively long roar (it should all even be clear that the phenomenon of reverberation adds to the length of the roar).
<p>For a wave propagating in a tube, the dispersion is directly related to the absorption coefficient since it can be written
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>k</mi>
            <mo class=MathClass-bin>+</mo>
            <mo class=MathClass-open>(</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <mi>j</mi>
            <mo class=MathClass-close>)</mo>
            <mi>α</mi>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   The presence of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
   </math>
   in the real part of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   reveals the dispersion phenomenon.
<h4 class=subsectionHead>Stationary waves with losses</h4>
<p>
   Consider a progressive wave
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
   </math>
   propagating to the right which reflects on a perfectly reflecting wall (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>) at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>. We have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mi>x</mi>
         </mrow>
      </msup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mi>x</mi>
         </mrow>
      </msup>
   </math>. Then, 
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi>A</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mi>x</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi>j</mi>
      <mi>A</mi>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mi>x</mi>
   </math>. The Standing Wave Ratio is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
      <mi>W</mi>
      <mi>R</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
                  <mi>a</mi>
                  <mi>x</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
                  <mi>i</mi>
                  <mi>n</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>. Pressure maxima are obtained for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ℜ</mi>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mi>n</mi>
      <mi>π</mi>
   </math>. Considering
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
      <mi>x</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
   </math>, pressure maxima are given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mi>a</mi>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≈</mo>
      <mo class=MathClass-rel>|</mo>
      <mi>A</mi>
      <mo class=MathClass-rel>|</mo>
   </math>. Minima are obtained by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ℜ</mi>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <mo class=MathClass-bin>+</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
      <mo class=MathClass-close>)</mo>
      <mi>π</mi>
   </math>
   and are given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mi>i</mi>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≈</mo>
      <mo class=MathClass-rel>|</mo>
      <mi>A</mi>
      <mo class=MathClass-rel>|</mo>
      <mi>α</mi>
      <mi>x</mi>
   </math>. Finally, the standing wave ratio is given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>S</mi>
            <mi>W</mi>
            <mi>R</mi>
            <mo class=MathClass-rel>≈</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>α</mi>
                  <mi>x</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(7)
</table>
<p>The standing wave ratio appears as inversely proportional to the absorption coefficient.
<h3 class=likesectionHead>Glossary</h3>
<p>
<div class=supertabular>
    <dl>
        <dt>SWR</dt>
        <dd>Standing Wave Ratio</dd>
    </dl>
</div>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt>[1]
   <dd> L. Brillouin, Wave propagation in periodic structures. MC Graw Hill (1946).
   <dt>[2]
   <dd>A. Pierce, Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York (1981)
   <dt>[3]
   <dd>E. Skudrzyk, The Foundations of Acoustics—Basic Mathematics and Basic Acoustics. Springer–Verlag (1971).
   <dt>[4]
   <dd>S. Temkin, Elements of Acoustics. Wiley, New York (1981).
</dl>`;

export default theory;
